/**
 * Page /grc/dossiers-scolaires — validation des dossiers d'inscription scolaire.
 *
 * Destinée au pôle GRC/communication (agent, manager, adjoints, maire) : voir les
 * dossiers soumis par les administrés, consulter les enfants déclarés, et valider
 * ou refuser. À la validation, l'administré est notifié (mail + push). Sur le modèle
 * du suivi des locations — unifié pour que le pôle GRC travaille au même endroit.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { GrcLayout } from '@/components/grc/GrcLayout';
import { Card, Button, Badge, Alert, LogoMark, SectionEyebrow } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { ThemeToggle } from '@/components/ThemeToggle';
import { GraduationCap, AlertCircle, Inbox, Check, X, Users, Settings, Plus } from 'lucide-react';
import type { DossierScolaire, DossierScolaireDetail, DossierScolaireStatut, DossierPieceDeposee } from '@roronoa-collectivites/shared-types';
import type { PieceType } from '@/lib/api';

const BUCKETS: { key: DossierScolaireStatut | 'tous'; label: string }[] = [
  { key: 'soumis', label: 'À traiter' },
  { key: 'en_attente', label: 'En attente' },
  { key: 'valide', label: 'Validés' },
  { key: 'refuse', label: 'Refusés' },
  { key: 'tous', label: 'Tous' },
];

function frDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
}

export function GrcDossiersScolairesPage() {
  const navigate = useNavigate();
  const { tenant, user } = useAuth();
  const peutValider = user ? ['super_admin', 'admin', 'manager'].includes(user.role) : false;
  const { confirm, dialogElement } = useConfirmDialog();
  const [items, setItems] = useState<DossierScolaire[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bucket, setBucket] = useState<DossierScolaireStatut | 'tous'>('soumis');

  const [detail, setDetail] = useState<DossierScolaireDetail | null>(null);
  const [pieces, setPieces] = useState<DossierPieceDeposee[]>([]);
  const [motifMode, setMotifMode] = useState<'refuse' | 'en_attente' | null>(null);
  const [motif, setMotif] = useState('');
  const [busy, setBusy] = useState(false);

  // Catalogue des pièces demandées (paramétrage) — système unifié piece_types.
  // On gère ici les pièces COMMUNES (servent scolaire + petite enfance) ; les
  // pièces spécifiques petite enfance se règlent sur la page Dossiers PE.
  const [configOpen, setConfigOpen] = useState(false);
  const [catalogue, setCatalogue] = useState<PieceType[]>([]);
  const [nouvellePiece, setNouvellePiece] = useState('');
  const [fenOuverture, setFenOuverture] = useState('');
  const [fenLimite, setFenLimite] = useState('');
  const [fenMsg, setFenMsg] = useState<string | null>(null);

  async function chargerCatalogue() {
    // typesAdmin('petite_enfance') renvoie commun + spécifiques PE ; on ne garde
    // que les COMMUNES pour cette page (les PE sont gérées sur la page PE).
    const all = (await api.famillePieces.typesAdmin('petite_enfance')).pieceTypes;
    setCatalogue(all.filter((t) => t.portee === 'commun'));
  }

  async function ouvrirConfig() {
    setConfigOpen(true); setFenMsg(null);
    try {
      await chargerCatalogue();
      const f = await api.scolaire.getFenetreInscription();
      setFenOuverture(f.ouverture ?? ''); setFenLimite(f.limite ?? '');
    }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function enregistrerFenetre() {
    setFenMsg(null);
    try {
      await api.scolaire.setFenetreInscription({ ouverture: fenOuverture || null, limite: fenLimite || null });
      setFenMsg('Période enregistrée.');
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function toggleType(t: PieceType) {
    try {
      await api.famillePieces.setTypeActif(t.id, !t.actif);
      setCatalogue((prev) => prev.map((x) => (x.id === t.id ? { ...x, actif: !x.actif } : x)));
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function ajouterPiece() {
    if (!nouvellePiece.trim()) return;
    try {
      const code = nouvellePiece.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '_').slice(0, 60);
      await api.famillePieces.createType({ code, libelle: nouvellePiece.trim(), portee: 'commun', module: null });
      setNouvellePiece('');
      await chargerCatalogue();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  async function load() {
    setLoading(true); setError(null);
    try { setItems(await api.scolaire.dossiers.list(bucket === 'tous' ? undefined : bucket)); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de chargement'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [bucket]);

  async function ouvrir(d: DossierScolaire) {
    setMotifMode(null); setMotif(''); setPieces([]);
    try {
      const [det, pcs] = await Promise.all([api.scolaire.dossiers.get(d.id), api.scolaire.dossiers.listPieces(d.id)]);
      setDetail(det); setPieces(pcs);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  async function ouvrirPiece(pieceId: string) {
    try { const { url } = await api.scolaire.dossiers.pieceUrl(pieceId); window.open(url, '_blank', 'noopener'); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  async function cocher(pieceId: string, conforme: boolean) {
    try {
      const updated = await api.scolaire.dossiers.cocherPiece(pieceId, conforme);
      setPieces((prev) => prev.map((p) => p.id === pieceId ? updated : p));
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  // Décision générique : valide / refuse / en_attente / soumis (ré-ouverture).
  // refuse et en_attente peuvent porter un motif (obligatoire pour refuse).
  async function decider(statut: 'valide' | 'refuse' | 'en_attente' | 'soumis', avecMotif = false) {
    if (!detail) return;
    if (statut === 'refuse' && !motif.trim()) { setError('Le motif de refus est obligatoire.'); return; }
    setBusy(true); setError(null);
    try {
      await api.scolaire.dossiers.valider(detail.id, statut, avecMotif ? motif.trim() || undefined : undefined);
      setDetail(null); setMotifMode(null); setMotif(''); await load();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setBusy(false); }
  }

  const statutBadge = (s: DossierScolaireStatut) => {
    const map: Record<DossierScolaireStatut, string> = { brouillon: 'Brouillon', soumis: 'À traiter', valide: 'Validé', refuse: 'Refusé', en_attente: 'En attente de pièces' };
    return <Badge>{map[s]}</Badge>;
  };

  return (
    <GrcLayout active="dossiers">
      <main id="main-content" className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10 animate-fade-in">
        <div className="mb-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <LogoMark icon={GraduationCap} accent="grc" size="lg" />
            <div>
              <SectionEyebrow as="div">Gestion des dossiers scolaires</SectionEyebrow>
              <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">Dossiers d'inscription</h1>
            </div>
          </div>
          {peutValider && (
            <Button variant="secondary" size="sm" onClick={ouvrirConfig}>
              <Settings className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Dossier type</span>
            </Button>
          )}
        </div>

        {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}

        <div className="mb-4 flex flex-wrap gap-1.5">
          {BUCKETS.map((b) => (
            <button key={b.key} type="button" onClick={() => setBucket(b.key)}
              className={['inline-flex h-9 items-center rounded-r-pill px-4 text-sm font-medium transition-all',
                bucket === b.key ? 'bg-grc text-white shadow-soft' : 'text-ink-muted hover:bg-surface/70'].join(' ')}>
              {b.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-sm text-ink-muted">Chargement…</p>
        ) : !items || items.length === 0 ? (
          <Card>
            <div className="flex flex-col items-center gap-3 p-8 text-center">
              <Inbox className="h-8 w-8 text-ink-subtle" />
              <div>
                <p className="text-sm font-medium text-ink">Aucun dossier dans cette vue.</p>
                <p className="mt-1 text-sm text-ink-muted">
                  Les familles déposent leur dossier depuis leur espace. Commencez par définir
                  le <strong>dossier type de votre commune</strong> : la liste des pièces que vous demandez.
                </p>
              </div>
              {peutValider ? (
                <Button variant="primary" size="sm" onClick={ouvrirConfig}>
                  <Settings className="h-4 w-4" /> Configurer les pièces demandées
                </Button>
              ) : (
                <p className="text-xs text-ink-subtle">La configuration des dossiers est gérée par un responsable.</p>
              )}
            </div>
          </Card>
        ) : (
          <ul className="space-y-2">
            {items.map((d) => (
              <li key={d.id}>
                <button type="button" onClick={() => ouvrir(d)} className="w-full text-left">
                  <Card>
                    <div className="flex items-center justify-between gap-3 p-4 transition-colors hover:bg-surface/40">
                      <div className="flex items-center gap-2">
                        <Users className="h-5 w-5 text-grc-fg" aria-hidden="true" />
                        <div>
                          <div className="text-sm font-medium text-ink">Famille {d.familleNom}</div>
                          <div className="text-xs text-ink-subtle">{d.nbEnfants ?? 0} enfant(s) · {d.anneeScolaire} · soumis le {frDate(d.soumisAt)}</div>
                        </div>
                      </div>
                      {statutBadge(d.statut)}
                    </div>
                  </Card>
                </button>
              </li>
            ))}
          </ul>
        )}
      </main>

      {/* Dialog détail + validation */}
      <Dialog open={!!detail} onClose={() => { setDetail(null); setMotifMode(null); }} title={detail ? `Famille ${detail.familleNom}` : ''} size="lg"
        footer={!detail ? null : !peutValider ? (
          <Button variant="ghost" onClick={() => setDetail(null)}>Fermer</Button>
        ) : motifMode ? (
          <>
            <Button variant="ghost" onClick={() => { setMotifMode(null); setMotif(''); }}>Retour</Button>
            {motifMode === 'refuse' ? (
              <Button variant="danger" loading={busy} onClick={() => decider('refuse', true)} disabled={!motif.trim()}>Confirmer le refus</Button>
            ) : (
              <Button variant="primary" loading={busy} onClick={() => decider('en_attente', true)}>Mettre en attente</Button>
            )}
          </>
        ) : detail.statut === 'soumis' || detail.statut === 'en_attente' ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" onClick={() => setMotifMode('refuse')}><X className="h-4 w-4" /> Refuser</Button>
            <Button variant="secondary" onClick={() => setMotifMode('en_attente')}>En attente de pièces</Button>
            <Button variant="primary" loading={busy} onClick={() => decider('valide')}><Check className="h-4 w-4" /> Valider</Button>
          </div>
        ) : (
          // Dossier déjà validé ou refusé : on peut revenir en arrière (ré-ouvrir)
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" onClick={() => setDetail(null)}>Fermer</Button>
            <Button variant="secondary" loading={busy} onClick={() => decider('soumis')}>Ré-ouvrir le dossier</Button>
          </div>
        )}>
        {detail && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-ink-muted">
              {statutBadge(detail.statut)}
              <span>Année {detail.anneeScolaire}</span>
            </div>
            {detail.motifRefus && <Alert variant="warning">Motif précédent : {detail.motifRefus}</Alert>}
            <div>
              <h3 className="mb-2 text-sm font-semibold text-ink">Enfants déclarés</h3>
              {detail.enfants.length === 0 ? (
                <p className="text-sm text-ink-muted">Aucun enfant déclaré.</p>
              ) : (
                <ul className="space-y-1.5">
                  {detail.enfants.map((e) => (
                    <li key={e.id} className="flex items-center justify-between rounded-r-md border border-border bg-surface/40 px-3 py-2">
                      <span className="text-sm text-ink">{e.prenom} {e.nom}</span>
                      <Badge>{e.statutDossier === 'valide' ? 'Validé' : 'En attente'}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h3 className="mb-2 text-sm font-semibold text-ink">Pièces déposées</h3>
              {pieces.length === 0 ? (
                <p className="text-sm text-ink-muted">Aucune pièce déposée.</p>
              ) : (
                <ul className="space-y-1.5">
                  {pieces.map((p) => (
                    <li key={p.id} className="flex items-center justify-between gap-2 rounded-r-md border border-border bg-surface/40 px-3 py-2">
                      <button type="button" onClick={() => ouvrirPiece(p.id)} className="min-w-0 text-left text-sm text-grc-fg underline-offset-2 hover:underline">
                        {p.nomFichier}
                      </button>
                      <div className="flex shrink-0 gap-1">
                        <button type="button" onClick={() => cocher(p.id, true)}
                          className={['inline-flex h-7 w-7 items-center justify-center rounded-r-md border transition-all',
                            p.conforme === true ? 'border-success/50 bg-success/15 text-success' : 'border-border text-ink-subtle hover:bg-surface/60'].join(' ')}
                          aria-label="Marquer conforme"><Check className="h-3.5 w-3.5" /></button>
                        <button type="button" onClick={() => cocher(p.id, false)}
                          className={['inline-flex h-7 w-7 items-center justify-center rounded-r-md border transition-all',
                            p.conforme === false ? 'border-danger/50 bg-danger/15 text-danger' : 'border-border text-ink-subtle hover:bg-surface/60'].join(' ')}
                          aria-label="Marquer non conforme"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {motifMode && (
              <div>
                <label htmlFor="motif" className="mb-1.5 block text-sm font-medium text-ink">
                  {motifMode === 'refuse' ? 'Motif du refus (envoyé au parent)' : 'Précision sur les pièces manquantes (optionnel, envoyée au parent)'}
                </label>
                <textarea id="motif" value={motif} onChange={(e) => setMotif(e.target.value)} rows={3}
                  className="w-full rounded-r-lg border border-border bg-surface/60 p-3 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg"
                  placeholder={motifMode === 'refuse' ? 'Ex. justificatif de domicile non conforme' : 'Ex. il manque le livret de famille'} />
              </div>
            )}
          </div>
        )}
      </Dialog>
      {/* Dialog configuration des pièces demandées */}
      <Dialog open={configOpen} onClose={() => setConfigOpen(false)} title="Dossier type — pièces demandées aux familles" size="lg"
        footer={<Button variant="ghost" onClick={() => setConfigOpen(false)}>Fermer</Button>}>
        <div className="space-y-4">
          {/* Période d'inscription (lot 3) */}
          <div className="rounded-r-lg border border-border bg-surface/40 p-3">
            <h4 className="mb-1 font-display text-sm font-semibold text-ink">Période d'inscription</h4>
            <p className="mb-3 text-xs text-ink-muted">Hors de cette période, les familles ne peuvent plus ajouter d'enfant (le bouton est grisé). Laissez vide pour ne pas restreindre.</p>
            {fenMsg && <Alert variant="success" className="mb-2">{fenMsg}</Alert>}
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label htmlFor="fen-o" className="mb-1.5 block text-xs font-medium text-ink">Ouverture</label>
                <input id="fen-o" type="date" value={fenOuverture} onChange={(e) => setFenOuverture(e.target.value)}
                  className="h-11 rounded-r-lg border border-border bg-surface/60 px-3 text-sm text-ink" />
              </div>
              <div>
                <label htmlFor="fen-l" className="mb-1.5 block text-xs font-medium text-ink">Date limite</label>
                <input id="fen-l" type="date" value={fenLimite} onChange={(e) => setFenLimite(e.target.value)}
                  className="h-11 rounded-r-lg border border-border bg-surface/60 px-3 text-sm text-ink" />
              </div>
              <Button variant="primary" onClick={enregistrerFenetre}>Enregistrer la période</Button>
            </div>
          </div>

          <p className="text-sm text-ink-muted">Activez les pièces que les familles doivent fournir. Toute pièce activée est obligatoire. Une pièce déposée sert tous les dossiers de la famille (scolaire, cantine, petite enfance).</p>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label htmlFor="np" className="mb-1.5 block text-sm font-medium text-ink">Créer une pièce sur mesure</label>
              <input id="np" value={nouvellePiece} onChange={(e) => setNouvellePiece(e.target.value)} placeholder="Justificatif de domicile"
                className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg" />
            </div>
            <Button variant="primary" onClick={ajouterPiece} disabled={!nouvellePiece.trim()}><Plus className="h-4 w-4" /> Ajouter</Button>
          </div>
          {catalogue.length === 0 ? (
            <p className="text-sm text-ink-subtle">Aucune pièce dans le catalogue.</p>
          ) : (
            <ul className="space-y-1.5">
              {catalogue.map((t) => (
                <li key={t.id} className={`flex items-center justify-between gap-3 rounded-r-md border border-border bg-surface/40 px-3 py-2 ${t.actif ? '' : 'opacity-60'}`}>
                  <span className="text-sm text-ink">{t.libelle}{t.actif && <span className="ml-1 text-xs text-ink-subtle">(demandée · obligatoire)</span>}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={t.actif}
                    aria-label={`${t.actif ? 'Ne plus demander' : 'Demander'} ${t.libelle}`}
                    onClick={() => void toggleType(t)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full border transition-colors ${t.actif ? 'border-grc/60 bg-grc/70' : 'border-border bg-surface-hi ring-1 ring-inset ring-border'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-soft transition-transform ${t.actif ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Dialog>
      {dialogElement}
    </GrcLayout>
  );
}
